import { motion } from "framer-motion";
import { ShieldCheck, Percent, Zap, ArrowRight } from "lucide-react";
import Reveal, { Eyebrow, EASE } from "@/components/Reveal";
import { scrollToTarget } from "@/lib/scroll";

function FeeBars() {
  return (
    <div className="space-y-3">
      {[
        { label: "Typical processor", v: "3.0%", w: "100%", green: false },
        { label: "B&W Pay", v: "1%", w: "33.3%", green: true },
      ].map((b, i) => (
        <div key={b.label}>
          <div className="mb-1.5 flex justify-between font-mono text-[11px]">
            <span className="text-mut">{b.label}</span>
            <span className={b.green ? "text-sgreen" : "text-white/70"}>{b.v}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.2, delay: 0.15 + i * 0.2, ease: EASE }}
              style={{ width: b.w, transformOrigin: "left" }}
              className={`h-full rounded-full ${b.green ? "bg-sgreen" : "bg-white/25"}`}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function WalletFlow() {
  return (
    <div className="flex items-center gap-3 font-mono text-[11px]">
      <span className="rounded-md border border-line bg-white/[0.03] px-2.5 py-1.5 text-white/80">
        Customer
      </span>
      <span className="relative h-px flex-1 bg-line">
        <motion.span
          initial={{ left: "0%" }}
          animate={{ left: ["0%", "100%"] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
          className="absolute -top-[2.5px] h-[6px] w-[6px] rounded-full bg-sgreen"
        />
      </span>
      <span className="rounded-md border border-sgreen/30 bg-sgreen/10 px-2.5 py-1.5 text-sgreen">
        Your wallet
      </span>
    </div>
  );
}

const FEATURES = [
  {
    id: "non-custodial",
    icon: ShieldCheck,
    title: "Non-Custodial",
    desc: "Funds go directly to your wallet. We never touch your money.",
    visual: <WalletFlow />,
  },
  {
    id: "flat-fee",
    icon: Percent,
    title: "1% Flat Fee",
    desc: "No hidden charges. No monthly fees. Just 1% per transaction.",
    visual: <FeeBars />,
  },
  {
    id: "instant-settlement",
    icon: Zap,
    title: "Instant Settlement",
    desc: "USDC lands in your wallet in under 2 seconds on Solana.",
    visual: (
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-3xl font-semibold text-sgreen">1.2s</span>
        <span className="font-mono text-[11px] uppercase tracking-widest text-mut">
          median finality
        </span>
      </div>
    ),
  },
];

export default function Features() {
  return (
    <section id="features" className="scroll-mt-24 py-24 sm:py-32" data-testid="features-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Why B&W Pay</Eyebrow>
              <h2 className="mt-4 max-w-xl text-3xl font-semibold tracking-tight sm:text-4xl">
                Built for merchants, not middlemen.
              </h2>
            </div>
            <button
              onClick={() => scrollToTarget("#how-it-works")}
              data-testid="features-see-how-button"
              className="group hidden items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mut transition-colors hover:text-sgreen sm:inline-flex"
            >
              See how it works
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
          {FEATURES.map((f, i) => (
            <Reveal key={f.id} delay={i * 0.12} className="h-full">
              <div
                data-testid={`feature-card-${f.id}`}
                className="card-hover group flex h-full flex-col justify-between rounded-2xl border border-line bg-card2 p-6 sm:p-8"
              >
                <div>
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-white/[0.03] transition-colors duration-500 group-hover:border-sgreen/40">
                    <f.icon className="h-5 w-5 text-sgreen" />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-white sm:text-2xl">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-mut sm:text-base">{f.desc}</p>
                </div>
                <div className="mt-8 rounded-xl border border-line/70 bg-panel/70 p-4">
                  {f.visual}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
